// apps/admin/src/presentation/merchants/components/StaffTab.tsx
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Loader2, Plus, KeyRound } from "lucide-react";
import { useStateMachineValue } from "@/core/arch/state_machine";
import { StaffListBloc } from "@/application/staff/staff_list_bloc";
import { StaffRepository } from "@/application/staff/staff_repository";
import type { StaffRow } from "@/application/staff/types";
import { Button } from "@/components/ui/base/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/base/dialog";
import { StaffFormSheet } from "./StaffFormSheet";

export function StaffTab({ businessId }: { businessId: string }) {
  const repo = useMemo(() => new StaffRepository(), []);
  // Rebuild the bloc when businessId changes — the merchant route swaps the
  // param without remounting, so a once-created bloc would keep querying the
  // first merchant. useStateMachineValue re-subscribes when the instance changes.
  const bloc = useMemo(
    () => new StaffListBloc(repo, businessId),
    [repo, businessId],
  );
  const [state] = useStateMachineValue(bloc);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<StaffRow | null>(null);
  const [saving, setSaving] = useState(false);
  // Row whose deactivation is awaiting confirmation, and the row currently
  // being mutated — the latter disables that row's actions so a double-click
  // can't fire concurrent updates that race the reload.
  const [pendingDeactivate, setPendingDeactivate] = useState<StaffRow | null>(
    null,
  );
  const [actingId, setActingId] = useState<string | null>(null);

  useEffect(() => {
    void bloc.load();
  }, [bloc]);

  function openCreate() {
    setEditing(null);
    setSheetOpen(true);
  }

  function openEdit(row: StaffRow) {
    setEditing(row);
    setSheetOpen(true);
  }

  async function handleSubmit(values: {
    name: string;
    email: string | null;
    isOwner: boolean;
    isActive: boolean;
    permissions: StaffRow["permissions"];
    pin?: string;
  }) {
    setSaving(true);
    try {
      if (editing) {
        await repo.update(businessId, { staffId: editing.id, ...values });
        toast.success("Staff member updated");
      } else {
        if (!values.pin) throw new Error("A 4-digit PIN is required");
        await repo.create(businessId, { ...values, pin: values.pin });
        toast.success("Staff member created");
      }
      setSheetOpen(false);
      await bloc.load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save staff");
    } finally {
      setSaving(false);
    }
  }

  async function runToggle(row: StaffRow) {
    if (actingId) return;
    setActingId(row.id);
    try {
      await repo.update(businessId, {
        staffId: row.id,
        isActive: !row.isActive,
      });
      toast.success(row.isActive ? "Staff deactivated" : "Staff activated");
      setPendingDeactivate(null);
      await bloc.load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update staff");
    } finally {
      setActingId(null);
    }
  }

  // Deactivating disables a merchant's POS login, so confirm first.
  // Reactivating is harmless and runs immediately.
  function onToggleClick(row: StaffRow) {
    if (row.isActive) {
      setPendingDeactivate(row);
    } else {
      void runToggle(row);
    }
  }

  if (state.status === "loading" || state.status === "idle") {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <Loader2 className="text-subtle-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="bg-card rounded-lg border p-8 text-center text-red-600">
        {state.error ?? "Failed to load staff"}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-foreground text-sm font-semibold">
          Staff ({state.items.length})
        </h2>
        <button
          onClick={openCreate}
          className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add staff
        </button>
      </div>

      {state.items.length === 0 ? (
        <div className="bg-card text-muted-foreground rounded-lg border p-8 text-center">
          <p className="text-sm">No staff yet.</p>
          <p className="text-sm">
            Add the first staff member (e.g. the owner) to get started.
          </p>
        </div>
      ) : (
        <div className="bg-card overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted text-muted-foreground border-b text-left text-xs">
              <tr>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Email</th>
                <th className="px-4 py-2 font-medium">Role</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Permissions</th>
                <th className="px-4 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {state.items.map((row) => {
                const perms = Object.entries(row.permissions)
                  .filter(([, v]) => v)
                  .map(([k]) => k.replace(/^can/, ""))
                  .join(", ");
                return (
                  <tr key={row.id} className="border-b last:border-0">
                    <td className="text-foreground px-4 py-2 font-medium">
                      {row.name}
                    </td>
                    <td className="text-muted-foreground px-4 py-2">
                      {row.email ?? "—"}
                    </td>
                    <td className="px-4 py-2">
                      {row.isOwner ? (
                        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">
                          Owner
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Staff</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={
                          row.isActive
                            ? "text-green-600"
                            : "text-subtle-foreground"
                        }
                      >
                        {row.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="text-muted-foreground px-4 py-2 text-xs">
                      {perms || "—"}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => openEdit(row)}
                          disabled={actingId === row.id}
                          className="text-blue-600 hover:text-blue-800 disabled:opacity-50"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => openEdit(row)}
                          disabled={actingId === row.id}
                          className="text-muted-foreground hover:text-foreground flex items-center gap-1 disabled:opacity-50"
                          title="Set or reset PIN"
                        >
                          <KeyRound className="h-3.5 w-3.5" />
                          PIN
                        </button>
                        <button
                          onClick={() => onToggleClick(row)}
                          disabled={actingId === row.id}
                          className="text-muted-foreground hover:text-foreground flex items-center gap-1 disabled:opacity-50"
                        >
                          {actingId === row.id && (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          )}
                          {row.isActive ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <StaffFormSheet
        open={sheetOpen}
        staff={editing}
        saving={saving}
        onClose={() => setSheetOpen(false)}
        onSubmit={(v) => void handleSubmit(v)}
      />

      <Dialog
        open={pendingDeactivate !== null}
        onOpenChange={(o) =>
          !o && actingId === null && setPendingDeactivate(null)
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deactivate staff member</DialogTitle>
            <DialogDescription>
              Deactivate <strong>{pendingDeactivate?.name}</strong>? They will
              no longer be able to sign in to the POS terminal. You can
              reactivate them later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingDeactivate(null)}
              disabled={actingId !== null}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                pendingDeactivate && void runToggle(pendingDeactivate)
              }
              disabled={actingId !== null}
            >
              {actingId !== null && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              Deactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
